/**
 * Predefined accident-description templates.
 *
 * Each template is a function that receives the *whole* case object and
 * returns a German-language draft sentence. It pulls in already-entered
 * fields (customer, insurance, vehicle, …) so the user only has to fine-
 * tune the resulting text.
 *
 * Used by `CaseEditor → Schadenbeschreibung-Dropdown` to seed the textarea.
 */

// Helpers — defensive accessors that fall back to neutral placeholders
const safe = (v, fb = "—") => (v && String(v).trim() ? String(v).trim() : fb);
const fullName = (c) => {
  const first = safe(c?.customer?.firstname, "");
  const last  = safe(c?.customer?.name, "");
  return `${first} ${last}`.trim() || "der Auftraggeber";
};
const vehicleLabel = (c) => {
  const make  = safe(c?.vehicle?.make, "");
  const model = safe(c?.vehicle?.model, "");
  const plate = safe(c?.vehicle?.license_plate, "");
  const v = `${make} ${model}`.trim();
  return v && plate
    ? `${v} (Kennzeichen ${plate})`
    : v || (plate ? `Fahrzeug mit Kennzeichen ${plate}` : "des Fahrzeugs");
};
const insuranceLabel = (c) => {
  const ins = safe(c?.insurance?.insurer_name, "");
  const claim = safe(c?.insurance?.claim_number, "");
  if (!ins && !claim) return "";
  if (ins && claim)  return ` Die zuständige Versicherung ist die ${ins} unter der Schaden-Nr. ${claim}.`;
  if (ins)           return ` Die zuständige Versicherung ist die ${ins}.`;
  return ` Die Schaden-Nr. lautet ${claim}.`;
};
const dateLabel = (c) => {
  const raw = c?.insurance?.claim_date;
  if (!raw) return "an einem nicht näher angegebenen Tag";
  try {
    const d = new Date(raw);
    return `am ${d.toLocaleDateString("de-DE")}`;
  } catch { return `am ${raw}`; }
};
const opp = (c) => {
  const name = safe(c?.customer?.opponent_name, "");
  const plate = safe(c?.customer?.opponent_plate, "");
  if (name && plate) return `${name} (Kennzeichen ${plate})`;
  if (name)          return name;
  if (plate)         return `Halter des Fahrzeugs mit Kennzeichen ${plate}`;
  return "der Unfallgegner";
};

// ─── Template registry ──────────────────────────────────────────────
export const ACCIDENT_TEMPLATES = [
  {
    key: "auffahrunfall",
    label: "Auffahrunfall (Heckkollision)",
    build: (c) =>
`Unfallhergang:
${dateLabel(c)} ereignete sich ein Auffahrunfall, bei dem das Fahrzeug ${vehicleLabel(c)} im Heckbereich beschädigt wurde. ${fullName(c)} stand verkehrsbedingt mit seinem Fahrzeug, als ${opp(c)} aus Unaufmerksamkeit auf das Heck auffuhr.

Schadenbild:
Sichtbare Beschädigungen befinden sich am Heck des Fahrzeugs (u. a. Stoßstange hinten, Kofferraumdeckel und Rückleuchten). Mögliche Folgeschäden an der Heckklappenmechanik, dem Auspuffsystem sowie der Karosseriestruktur sind in einer Werkstattuntersuchung abzuklären.${insuranceLabel(c)}`
  },
  {
    key: "frontalzusammenstoss",
    label: "Frontalzusammenstoß",
    build: (c) =>
`Unfallhergang:
${dateLabel(c)} kam es zu einem frontalen Zusammenstoß zwischen dem Fahrzeug ${vehicleLabel(c)} (geführt von ${fullName(c)}) und dem Fahrzeug von ${opp(c)}. Die Kollision erfolgte mit der Fahrzeugfront beider Fahrzeuge.

Schadenbild:
Schäden befinden sich im gesamten Frontbereich (Stoßstange vorne, Motorhaube, Kühlergrill, Scheinwerfer, ggf. Kühler und Frontradträger). Aufgrund der Anstoßenergie sind verdeckte Schäden am Längsträger, Airbag-Sensor sowie an der Crashstruktur zu prüfen.${insuranceLabel(c)}`
  },
  {
    key: "seitlich_t_bone",
    label: "Seitlicher Zusammenstoß (T-Bone-Unfall)",
    build: (c) =>
`Unfallhergang:
${dateLabel(c)} ereignete sich ein seitlicher Zusammenstoß. Das Fahrzeug ${vehicleLabel(c)} wurde von ${opp(c)} im rechten/linken Winkel an der Fahrer- bzw. Beifahrerseite getroffen (klassischer „T-Bone-Unfall").

Schadenbild:
Erhebliche Beschädigungen entlang der seitlichen Karosserie (Türen, B-Säule, Schweller, Kotflügel). Die strukturelle Integrität ist zu prüfen — ggf. Funktionseinschränkungen an Seitenairbag, Türschloss und Fensterheber. Eine Vermessung des Fahrzeugrahmens wird empfohlen.${insuranceLabel(c)}`
  },
  {
    key: "spurwechsel",
    label: "Spurwechselunfall",
    build: (c) =>
`Unfallhergang:
${dateLabel(c)} kam es zu einem Spurwechselunfall. ${opp(c)} wechselte unter Missachtung des nachfolgenden Verkehrs auf die von ${fullName(c)} befahrene Fahrspur und kollidierte seitlich mit dem Fahrzeug ${vehicleLabel(c)}.

Schadenbild:
Streif- und Aufprallschäden an der seitlichen Karosserie (Stoßstange, Kotflügel, Tür). Die Lackoberfläche ist großflächig zerstört, einzelne Anbauteile sind verformt.${insuranceLabel(c)}`
  },
  {
    key: "ueberholunfall",
    label: "Überholunfall",
    build: (c) =>
`Unfallhergang:
${dateLabel(c)} ereignete sich ein Unfall im Zusammenhang mit einem Überholmanöver. ${opp(c)} überholte das Fahrzeug ${vehicleLabel(c)} und kollidierte beim Wiedereinscheren bzw. während des Überholvorgangs mit ${fullName(c)}s Fahrzeug.

Schadenbild:
Schäden überwiegend im seitlich-vorderen Bereich auf der Überholseite (Kotflügel, Außenspiegel, Tür, ggf. Stoßstange).${insuranceLabel(c)}`
  },
  {
    key: "streifunfall",
    label: "Streifunfall",
    build: (c) =>
`Unfallhergang:
${dateLabel(c)} ereignete sich ein Streifunfall, bei dem ${opp(c)} mit seinem Fahrzeug das Fahrzeug ${vehicleLabel(c)} entlang der Längsseite touchierte.

Schadenbild:
Lange, oberflächliche Streif- und Schleifspuren an der seitlichen Karosserie (Türen, Kotflügel, Schweller). Spuren von Fremdlack und Materialabtrag sind erkennbar.${insuranceLabel(c)}`
  },
  {
    key: "abbiegeunfall",
    label: "Abbiegeunfall (links/rechts)",
    build: (c) =>
`Unfallhergang:
${dateLabel(c)} ereignete sich ein Abbiegeunfall. ${opp(c)} bog (links/rechts) ab und missachtete dabei den fließenden Verkehr, in dem sich das Fahrzeug ${vehicleLabel(c)} (geführt von ${fullName(c)}) befand.

Schadenbild:
Beschädigungen im Front- bzw. Seitenbereich (Stoßstange, Kotflügel, Scheinwerfer, Tür). Die genaue Aufprallstelle ist abhängig von der Fahrtrichtung der Beteiligten.${insuranceLabel(c)}`
  },
  {
    key: "kreuzungsunfall",
    label: "Kreuzungsunfall",
    build: (c) =>
`Unfallhergang:
${dateLabel(c)} kam es im Bereich einer Kreuzung zu einer Kollision zwischen dem Fahrzeug ${vehicleLabel(c)} (geführt von ${fullName(c)}) und ${opp(c)}.

Schadenbild:
Aufprallschäden im Front- und/oder Seitenbereich des Fahrzeugs. Aufgrund der Geschwindigkeitskomponenten an Kreuzungen sind verdeckte Strukturschäden zu prüfen.${insuranceLabel(c)}`
  },
  {
    key: "vorfahrt",
    label: "Vorfahrtmissachtung",
    build: (c) =>
`Unfallhergang:
${dateLabel(c)} missachtete ${opp(c)} die Vorfahrt des Fahrzeugs ${vehicleLabel(c)}. Es kam zur Kollision, ${fullName(c)} hatte keine Möglichkeit zur Schadenabwehr.

Schadenbild:
Aufprallschäden im Front- bzw. Seitenbereich. Die Schwere der Beschädigung deutet auf eine deutliche Restgeschwindigkeit zum Kollisionszeitpunkt hin.${insuranceLabel(c)}`
  },
  {
    key: "rotlicht",
    label: "Rotlichtverstoß",
    build: (c) =>
`Unfallhergang:
${dateLabel(c)} überfuhr ${opp(c)} eine Lichtzeichenanlage trotz Rotlicht und kollidierte mit dem Fahrzeug ${vehicleLabel(c)} (geführt von ${fullName(c)}), das die Kreuzung bei Grün passierte.

Schadenbild:
Erhebliche Schäden im Front- oder Seitenbereich, abhängig vom Kollisionswinkel. Strukturelle Schäden sind nicht ausgeschlossen — eine Werkstattvermessung wird empfohlen.${insuranceLabel(c)}`
  },
  {
    key: "parkrempler",
    label: "Parkunfall / Parkrempler",
    build: (c) =>
`Unfallhergang:
${dateLabel(c)} ereignete sich an dem ordnungsgemäß abgestellten Fahrzeug ${vehicleLabel(c)} ein Parkrempler. ${opp(c)} kollidierte beim Vorbeifahren bzw. Rangieren mit dem ruhenden Fahrzeug.

Schadenbild:
Lokale Beschädigungen an der getroffenen Karosserieseite (z. B. Stoßstange, Tür, Kotflügel). Lack-, Druck- und Verformungsschäden sind im Detail dokumentiert.${insuranceLabel(c)}`
  },
  {
    key: "tueroeffnung",
    label: "Türöffnungsunfall",
    build: (c) =>
`Unfallhergang:
${dateLabel(c)} öffnete ${opp(c)} an seinem geparkten Fahrzeug die Fahrer- bzw. Beifahrertür, ohne auf den fließenden Verkehr zu achten. Hierdurch kam es zur Kollision mit dem vorbeifahrenden Fahrzeug ${vehicleLabel(c)} von ${fullName(c)}.

Schadenbild:
Streif- und Aufprallschäden an der seitlichen Karosserie (Tür, Kotflügel, Schweller, Spiegel). Funktionsstörungen am Türblech bzw. Türmechanismus sind möglich.${insuranceLabel(c)}`
  },
  {
    key: "ein_aus_parken",
    label: "Unfall beim Ein- oder Ausparken",
    build: (c) =>
`Unfallhergang:
${dateLabel(c)} kam es beim Ein- bzw. Ausparken zu einer Kollision zwischen dem Fahrzeug ${vehicleLabel(c)} und dem Fahrzeug von ${opp(c)}.

Schadenbild:
Niedrige Geschwindigkeit, jedoch ggf. erhebliche Lack- und Verformungsschäden im getroffenen Bereich (Stoßstange, Kotflügel, Tür). Anbauteile wie Außenspiegel können in Mitleidenschaft gezogen sein.${insuranceLabel(c)}`
  },
  {
    key: "rueckwaerts",
    label: "Rückwärtsfahrunfall",
    build: (c) =>
`Unfallhergang:
${dateLabel(c)} fuhr ${opp(c)} rückwärts und übersah dabei das Fahrzeug ${vehicleLabel(c)} von ${fullName(c)}. Es kam zur Kollision.

Schadenbild:
Schäden im Front- bzw. Heckbereich des Fahrzeugs (je nach Fahrtrichtung), insbesondere an Stoßstange, Kennzeichenhalter und Scheinwerfer/Rückleuchten.${insuranceLabel(c)}`
  },
];

/** Returns the rendered template text for the given key & case data. */
export function buildAccidentText(key, caseObj) {
  const tpl = ACCIDENT_TEMPLATES.find((t) => t.key === key);
  return tpl ? tpl.build(caseObj || {}) : "";
}
